import type { IBaseControl } from '@eureka/utils/control';
import _ from 'lodash';
import qs from 'qs';
import { container } from '../container';
import type { ICoreDataWrapper } from '../exception';
import { MergeApi } from '../request.decorator';
import type { IQueryRoot } from '../types/query';
import type { ISolution } from '../types/solution';
import {
    type IAmountItem,
    type IClueInfo,
    type IGetActivityInitData,
    type IGetInviteRegisterInfoData,
    type IPackageType,
    type IPostSubmitFeedbackParams,
    type IUserInformation,
    type AccountInfo,
    type AccountInfoSave,
    ClueReasonOther,
    type LsPlgUsage,
    type IExperimentItem,
    type INeedAddTrialResponse,
} from './common.api.types';
import { isUserLogin } from '@eureka/utils/account';
export interface ISolutionProfileRequest {
    solution: { solution_id: string, solution_type: string }[],
    paper_fields?: string[],
    patent_fields?: string[],
    finally_query?: string,
    key_words?: string[],
    doc_lang?: string
    fields?: string[]
}
export class CommonApis {
    @MergeApi({ delay: 300 })
    static getSolutionData<T extends ISolution>({
        solution,
        fields,
        finally_query,
        key_words,
        doc_lang,
    }: ISolutionProfileRequest) {
        return container.RnDHttp.post<ICoreDataWrapper<T[]>>({
            url: `${process.env.VUE_APP_EUREKA_SERVICE_URI}/eureka/common/solution/specific-data`,
            data: {
                solution,
                fields,
                finally_query,
                key_words,
                doc_lang,
            },
        }).then(res => {
            return res.data
        })
    }

    // 添加试用
    static addTrial(empower_type: string, from?: string, data?: Record<string, any>) {
        return container.RnDHttp.post({
            url: `${process.env.VUE_APP_EUREKA_SERVICE_URI}/eureka/account-privilege/add-trial`,
            params: {
                empower_type,
                from,
            },
            data,
        })
    }

    static needAddTrial(empower_type: string, from?: string, data?: Record<string, any>) {
        const url = `${process.env.VUE_APP_EUREKA_SERVICE_URI}/eureka/account-privilege/product-onboarding/check`
        return container.RnDHttp.post<ICoreDataWrapper<INeedAddTrialResponse>>({
            url,
            params: {
                empower_type,
                from,
            },
            data,
        })
    }

    /**
     * 邀请注册入口初始化接口
     */
    static getInviteRegisterInfo() {
        const url = `${process.env.VUE_APP_EUREKA_SERVICE_URI}/eureka/activity/invite-register/info`
        return container.RnDHttp.get<ICoreDataWrapper<IGetInviteRegisterInfoData>>({
            url,
        })
    }

    /**
     * 获取用户激励反馈次数
     */
    static getFeedbackInfo() {
        const url = `${process.env.VUE_APP_EUREKA_SERVICE_URI}/eureka/activity/feedback/info`
        return container.RnDHttp.get<ICoreDataWrapper<number>>({
            url,
        })
    }

    /**
     * 用户提交激励反馈
     */
    static postSubmitFeedback(params: IPostSubmitFeedbackParams) {
        const url = `${process.env.VUE_APP_EUREKA_SERVICE_URI}/eureka/activity/feedback`
        return container.RnDHttp.post({
            url,
            data: params,
        })
    }

    /**
     * 获取公司信息
     */
    static getCompanyInfo(companyName: string) {
        const params = {
            companyName,
        }
        const url = `${process.env.VUE_APP_PASSPORT_URI}/public/tyc/companies`
        return container.RnDHttp.get<string[]>({
            url,
            params,
            anonymous: true,
        })
    }

    /**
     * 获取领取状态
     */
    static getReceiveInit() {
        const url = `${process.env.VUE_APP_EUREKA_SERVICE_URI}/eureka/activity/receive/init`
        return container.RnDHttp.get<ICoreDataWrapper<IGetActivityInitData>>({
            url,
        })
    }

    /**
     * 获取用户基本信息
     */
    static getUserInfo(from = '') {
        const url = `${process.env.VUE_APP_EUREKA_SERVICE_URI}/eureka/activity/information/info`
        return container.RnDHttp.get<ICoreDataWrapper<IUserInformation>>({
            url,
            params: {
                from,
            },
        })
    }

    /**
     * 获取用户信息
     */
    static getAccountInfo() {
        const url = `${process.env.VUE_APP_EUREKA_SERVICE_URI}/eureka/account-info`
        return container.RnDHttp.get<ICoreDataWrapper<AccountInfo>>({
            url,
        }).then(res => res.data)
    }

    /**
     * plg完善用户信息解锁权益
     */
    static setUserInfo(accountInfo: AccountInfoSave) {
        const url = `${process.env.VUE_APP_EUREKA_SERVICE_URI}/eureka/save-user-information`
        const manual_company = accountInfo.company_name;
        return container.RnDHttp.post<ICoreDataWrapper<unknown>>({
            url,
            data: {
                ..._.omit(accountInfo, 'company_name'),
                manual_company,
            },
        }).then(res => res.data)
    }

    /**
     * 完善用户信息
     */
    static postPerfectInformation(data: IUserInformation) {
        const url = `${process.env.VUE_APP_EUREKA_SERVICE_URI}/eureka/activity/information`
        return container.RnDHttp.post<ICoreDataWrapper<any>>({
            url,
            data,
        })
    }

    /**
     * 获取公钥(游客)
     */
    static getPublicKeyApiByAnonymous() {
        const url = `${process.env.VUE_APP_PASSPORT_URI}/public/request_public_key`
        return container.RnDHttp.get({
            url,
            anonymous: true,
        })
    }

    /**
     * 发送短信验证码(游客)
     */
    static postMobileVerifyCodeApiByAnonymous(data: any) {
        const url = `${process.env.VUE_APP_PASSPORT_URI}/public/mobile-number/verify-code`
        return container.RnDHttp.postForm({
            url,
            anonymous: true,
            data: qs.stringify(data),
        })
    }

    /**
     * 获取公钥
     */
    static getPublicKeyApi() {
        const url = `${process.env.VUE_APP_PASSPORT_URI}/public/request_public_key`
        return container.RnDHttp.get({
            url,
        })
    }

    /**
     * 发送短信验证码
     */
    static postMobileVerifyCodeApi(data: any) {
        const url = `${process.env.VUE_APP_PASSPORT_URI}/public/mobile-number/verify-code`
        return container.RnDHttp.postForm({
            url,
            data: qs.stringify(data),
        })
    }

    /**
     * 领取活动奖励
     */
    static postReceiveAwardApi(activeType: any) {
        const url = `${process.env.VUE_APP_EUREKA_SERVICE_URI}/eureka/activity/receive-award?active_type=${activeType}`
        return container.RnDHttp.post<ICoreDataWrapper<any>>({
            url,
        })
    }

    static getGlobalControl() {
        const url = `${process.env.VUE_APP_EUREKA_SERVICE_URI}/eureka/amounts/control`
        return container.RnDHttp.get<ICoreDataWrapper<IBaseControl>>({
            url,
        }).then(res => {
            return res.data
        })
    }

    /**
     * 获取用户版本权限
     */
    static getUserPackageType() {
        const url = `${process.env.VUE_APP_EUREKA_SERVICE_URI}/eureka/package-info?refresh=true`
        return container.RnDHttp.get<ICoreDataWrapper<IPackageType[]>>({
            url,
            cache: true,
        }).then(res => {
            return res.data
        })
    }

    /**
     * 保存已引导过的页面
     */
    static saveGuidedPage(page: string) {
        const url = `${process.env.VUE_APP_EUREKA_SERVICE_URI}/eureka/save-guide-alert/${page}`
        return container.RnDHttp.post<ICoreDataWrapper<string>>({
            url,
        }).then(res => {
            return res.data
        })
    }

    /**
     * 引导界面埋点
     * @param isRD
     */
    static saveGuidedTrack(isRD: boolean) {
        const url = `${process.env.VUE_APP_EUREKA_SERVICE_URI}/eureka/tracking/guide?isRD=${!!isRD}`
        return container.RnDHttp.get<ICoreDataWrapper<string>>({
            url,
        }).then(res => {
            return res.data
        })
    }

    /**
     * 获取个人积分详情
     * GET /api/eureka/credits/personal/detail
     */
    static getPersonalCreditsDetail() {
        const url = `${process.env.VUE_APP_EUREKA_SERVICE_URI}/api/eureka/credits/personal/detail`;
        return container.RnDHttp.get<ICoreDataWrapper<{ total_credits: number; used_credits: number; daily_used_credits: number; items: any[] }>>({
            url,
        }).then(res => res.data);
    }

    /**
     * 获取公司积分详情
     * GET /api/eureka/credits/company/detail
     */
    static getCompanyCreditsDetail() {
        const url = `${process.env.VUE_APP_EUREKA_SERVICE_URI}/api/eureka/credits/company/detail`;
        return container.RnDHttp.get<ICoreDataWrapper<{ total_credits: number; used_credits: number; daily_used_credits: number; user_daily_limit: number; company_user_daily_limit: number; company_total_daily_limit: number; items: any[] }>>({
            url,
        }).then(res => res.data);
    }

    static getSuggestTopic() {
        const url = `${process.env.VUE_APP_EUREKA_SERVICE_URI}/eureka/smart/suggest/company-topic`;
        return container.RnDHttp.get<ICoreDataWrapper<string[]>>({
            url,
        }).then(res => {
            return res.data
        })
    }

    static track(data: {
        module_type: string,
        event_type: string,
        source_type: string,
        other_param?: string,
        event_detail_str?: string,
    }, allowAnonymous = false) {
        return container.RnDHttp.post<ICoreDataWrapper<any>>({
            anonymous: allowAnonymous,
            url: `${process.env.VUE_APP_EUREKA_SERVICE_URI}/eureka/tracking/add`,
            data,
        }).then(res => {
            return res.data
        })
    }

    /**
     *
     * @param queryData
     * @returns 换一个qid
     */
    static queryItemToShort(data: {
        q_s?: IQueryRoot,
        efq?: unknown
    }) {
        return container.RnDHttp.post<ICoreDataWrapper<{
            qid: string,
            efqid: string
        }>>({
            url: `${process.env.VUE_APP_EUREKA_SERVICE_URI}/eureka/web-query/transfer-to-short`,
            data: data,
        }).then(res => {
            return res.data
        })
    }

    /**
     * @param eureka 和 air入口切换
     */
    static setSelectedModule(module: string) {
        return container.RnDHttp.post({
            url: `${process.env.VUE_APP_EUREKA_SERVICE_URI}/eureka/save-selected-module/${module}`,
        }).then(res => {
            return res.data
        })
    }

    /**
     * 提交升级意向线索
     */
    static submitEurekaUpdateInfo(data: IClueInfo) {
        let reasonArr = data.reason;
        if (reasonArr.includes(ClueReasonOther)) {
            reasonArr = _.pull(reasonArr, ClueReasonOther);
            reasonArr.push(`${ClueReasonOther}:${data.otherReason}`);
        }
        return container.RnDHttp.post<ICoreDataWrapper<boolean>>({
            url: `${process.env.VUE_APP_EUREKA_SERVICE_URI}/eureka/lead/create`,
            anonymous: true,
            method: 'POST',
            data: {
                ...data,
                upgrade_reason: reasonArr.join('|'),
            },
        }).then(res => {
            return res.data
        })
    }

    /**
     * 获取用量信息
     */
    static getUsageAmount() {
        return container.RnDHttp.get<ICoreDataWrapper<Array<IAmountItem>>>({
            url: `${process.env.VUE_APP_EUREKA_SERVICE_URI}/eureka/amounts/view`,
        }).then(res => {
            return res.data
        })
    }

    static getUsageAmountPage() {
        return container.RnDHttp.get<ICoreDataWrapper<Array<IAmountItem>>>({
            url: `${process.env.VUE_APP_EUREKA_SERVICE_URI}/eureka/amounts/page`,
        }).then(res => {
            return res.data
        })
    }

    /**
     * 是否需要onBoarding
     */
    static getUserIsNeedOnBoarding() {
        return container.RnDHttp.get<ICoreDataWrapper<{ required: boolean, industry: string, role: string }>>({
            url: `${process.env.VUE_APP_EUREKA_SERVICE_URI}/eureka/rd-home-page/is-need-profiling`,
        }).then(res => {
            return res.data
        })
    }

    static getLsPlgUsage() {
        return container.RnDHttp.get<LsPlgUsage>({
            url: `${process.env.VUE_APP_EUREKA_SERVICE_URI}/core-eurekals-api/eurekals_agent/user/usage`,
        })
    }

    /**
     * 获取key的配置信息
     * @param experimentKey
     * @returns
     */
    static async getExperimentItem<K, V>(experimentKey: K) {
        const url = await isUserLogin() ? `${process.env.VUE_APP_EUREKA_SERVICE_URI}/core-eurekals-api/eurekals_agent/1.0/ab-test/group` : `${process.env.VUE_APP_EUREKA_SERVICE_URI}/core-eurekals-api/eurekals_agent/1.0/guest/ab-test/group`
        return container.RnDHttp.post<IExperimentItem<K, V>>({
            url,
            data: {
                experiment_key: experimentKey,
            },
        }).then(res => {
            return res
        })
    }
}
